import React, { useState, useEffect } from 'react';
import { AlertTriangle, Search } from 'lucide-react';
import axios from 'axios';

const BarrelToppingModal = ({ 
  show, 
  wineID, 
  wineContainers,
  onClose, 
  onSave 
}) => {
  const [toppingData, setToppingData] = useState({
    sourceBarrel: '',
    destinationBarrels: [],
    gallonsUsed: 0,
    notes: ''
  });
  const [wines, setWines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSourceWineID, setSelectedSourceWineID] = useState(wineID);
  const [allWineContainers, setAllWineContainers] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // New state variables for container reassignment
  const [showReassignmentSection, setShowReassignmentSection] = useState(false);
  const [remainingGallons, setRemainingGallons] = useState(0);
  const [selectedReassignments, setSelectedReassignments] = useState([]);
  const [standardContainerSizes] = useState([
    { name: '225L Barrel', gallons: 59 },
    { name: '15gal Stainless Keg', gallons: 15 },
    { name: '5gal Stainless Keg', gallons: 5 },
    { name: '1gal Glass Jug', gallons: 1 }
  ]);

  // Fetch all active wines when the modal opens
  useEffect(() => {
    if (show) {
      fetchWines();
    }
  }, [show]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setToppingData({
        sourceBarrel: '',
        destinationBarrels: [],
        gallonsUsed: 0,
        notes: ''
      });
      setSelectedSourceWineID(wineID);
      setSearchTerm('');
      setShowReassignmentSection(false);
      setRemainingGallons(0);
      setSelectedReassignments([]);
    } else {
      setSelectedSourceWineID(wineID);
    }
  }, [show, wineID]);

  // Fetch containers for the selected source wine
  useEffect(() => {
    if (selectedSourceWineID && show) {
      fetchContainersForWine(selectedSourceWineID);
    }
  }, [selectedSourceWineID, show]);

  // Calculate remaining gallons when source barrel and gallons used change
  useEffect(() => {
    if (selectedSourceWineID && toppingData.sourceBarrel && toppingData.gallonsUsed > 0) {
      const sourceBarrel = allWineContainers[selectedSourceWineID]?.find(
        c => c.containerID == toppingData.sourceBarrel
      );
      
      if (sourceBarrel) {
        const totalBarrelGallons = parseFloat(sourceBarrel.cntGallons) || 0;
        const gallonsUsed = parseFloat(toppingData.gallonsUsed) || 0;
        const remaining = Math.max(0, totalBarrelGallons - gallonsUsed);
        
        setRemainingGallons(remaining);
        setShowReassignmentSection(remaining > 0.5 && gallonsUsed > 0);
        
        if (remaining > 0.5) {
          suggestContainers(remaining);
        } else {
          setSelectedReassignments([]);
        }
      }
    } else {
      setShowReassignmentSection(false);
    }
  }, [toppingData.sourceBarrel, toppingData.gallonsUsed, selectedSourceWineID, allWineContainers]);

  const fetchWines = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/wines?active=1&limit=100');
      setWines(response.data.wines || []);
    } catch (error) {
      console.error('Error fetching wines:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContainersForWine = async (wineID) => {
    if (allWineContainers[wineID]) {
      return; // Already fetched
    }

    try {
      const response = await axios.get(`/api/wines/${wineID}/containers`);
      setAllWineContainers(prev => ({
        ...prev,
        [wineID]: response.data
      }));
    } catch (error) {
      console.error(`Error fetching containers for wine ${wineID}:`, error);
    }
  };

  // Function to suggest container distribution based on remaining gallons
  const suggestContainers = (gallons) => {
    let remaining = gallons;
    const suggestions = [];
    
    // Sort container sizes from largest to smallest
    const sortedSizes = [...standardContainerSizes].sort((a, b) => b.gallons - a.gallons);
    
    // Distribute remaining gallons to standard container sizes
    sortedSizes.forEach(container => {
      while (remaining >= container.gallons) {
        suggestions.push({
          containerType: container.name,
          gallons: container.gallons,
          selected: true
        });
        remaining -= container.gallons;
      }
    });
    
    // Add leftover if any
    if (remaining > 0.1) {
      suggestions.push({
        containerType: 'Leftover',
        gallons: parseFloat(remaining.toFixed(2)),
        selected: true
      });
    }
    
    setSelectedReassignments(suggestions);
  };

  // Function to handle container selection change in reassignment table
  const handleReassignmentChange = (index, checked) => {
    const updated = [...selectedReassignments];
    updated[index].selected = checked;
    setSelectedReassignments(updated);
  };

  const handleAddTopping = () => {
    // Get source barrel details
    const sourceWine = wines.find(w => w.wineID == selectedSourceWineID) || {};
    const sourceBarrelDetails = allWineContainers[selectedSourceWineID]?.find(c => c.containerID == toppingData.sourceBarrel);
    
    // Calculate the difference (negative since we're removing wine from source)
    const startGallons = sourceBarrelDetails?.cntGallons || 0;
    const endGallons = Math.max(0, startGallons - parseFloat(toppingData.gallonsUsed));
    const difference = endGallons - startGallons; // This will be negative

    // Get destination barrel IDs for a more detailed description
    const destinationBarrelIDs = toppingData.destinationBarrels.sort().join(', #');
    
    // Create a detailed description with specific barrel IDs
    const description = `Topped barrels #${destinationBarrelIDs} using ${toppingData.gallonsUsed} gallons from ${sourceWine.varietal} ${sourceWine.vintage} - container #${toppingData.sourceBarrel} (${sourceBarrelDetails?.cntDesc || 'Unknown'})`;
    
    // Create topping activity with reassignment data if needed
    const toppingActivity = {
      type: 'Topping',
      description: description,
      startGallons: startGallons,
      endGallons: endGallons,
      difference: difference,
      sourceWineID: selectedSourceWineID,
      sourceBarrel: toppingData.sourceBarrel,
      destinationBarrels: toppingData.destinationBarrels,
      notes: toppingData.notes,
      reassignments: showReassignmentSection ? 
        selectedReassignments.filter(r => r.selected) : []
    };
    
    onSave(toppingActivity);
  };

  // Filter barrels when searching
  const filteredBarrels = wineContainers[wineID]?.filter(container => {
    return searchTerm === '' || 
           container.cntDesc.toLowerCase().includes(searchTerm.toLowerCase()) ||
           container.containerID.toString().includes(searchTerm);
  }) || [];

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Barrel Topping</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info">
                <div className="d-flex">
                  <AlertTriangle className="me-2" size={18} />
                  <div>
                    Select a source barrel from any wine lot and destination barrels from the current wine lot for topping. 
                    The source barrel's volume will be reduced by the gallons used.
                  </div>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Source Wine Lot</label>
                  <select
                    className="form-control"
                    value={selectedSourceWineID}
                    onChange={(e) => {
                      setSelectedSourceWineID(e.target.value);
                      setToppingData({
                        ...toppingData,
                        sourceBarrel: '' // Reset selected barrel
                      });
                    }}
                    required
                  >
                    <option value="">Select Source Wine</option>
                    {wines.map((wine) => (
                      <option key={wine.wineID} value={wine.wineID}>
                        {wine.vintage} {wine.varietal} - {wine.vineyard || 'Unknown'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Source Barrel</label>
                  <select
                    className="form-control"
                    value={toppingData.sourceBarrel}
                    onChange={(e) => setToppingData({
                      ...toppingData,
                      sourceBarrel: e.target.value
                    })}
                    required
                    disabled={!selectedSourceWineID}
                  >
                    <option value="">Select Source Barrel</option>
                    {allWineContainers[selectedSourceWineID]?.map(container => (
                      <option 
                        key={container.containerID} 
                        value={container.containerID}
                      >
                        #{container.containerID} - {container.cntDesc} - {container.cntGallons} gal
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Gallons Used</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    value={toppingData.gallonsUsed}
                    onChange={(e) => setToppingData({
                      ...toppingData,
                      gallonsUsed: Math.max(0, parseFloat(e.target.value) || 0)
                    })}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    value={toppingData.notes}
                    onChange={(e) => setToppingData({
                      ...toppingData,
                      notes: e.target.value
                    })}
                    placeholder="Any additional notes"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Destination Barrels (Current Wine Lot)</label>
                <div className="input-group mb-3">
                  <span className="input-group-text">
                    <Search size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search barrels..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="card">
                  <div className="card-body">
                    <div className="row">
                      {filteredBarrels.length > 0 ? (
                        filteredBarrels.map(container => {
                          // Don't allow selecting the source barrel as a destination
                          const isDisabled = (container.containerID == toppingData.sourceBarrel && 
                                            selectedSourceWineID == wineID);
                          const isSelected = toppingData.destinationBarrels.includes(container.containerID);
                          
                          return (
                            <div key={container.containerID} className="col-md-4 mb-2">
                              <div className={`card ${isSelected ? 'bg-primary text-white' : isDisabled ? 'bg-light text-muted' : ''}`}>
                                <div className="card-body p-2">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`top-dest-${container.containerID}`}
                                      checked={isSelected}
                                      disabled={isDisabled}
                                      onChange={() => {
                                        const newDestinations = isSelected
                                          ? toppingData.destinationBarrels.filter(id => id !== container.containerID)
                                          : [...toppingData.destinationBarrels, container.containerID];
                                        
                                        setToppingData({
                                          ...toppingData,
                                          destinationBarrels: newDestinations
                                        });
                                      }}
                                    />
                                    <label 
                                      className={`form-check-label ${isDisabled ? 'text-muted' : ''}`} 
                                      htmlFor={`top-dest-${container.containerID}`}
                                    >
                                      <strong>#{container.containerID} - {container.cntDesc}</strong> - {container.cntGallons} gal
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="col-12 text-center py-3">
                          <p className="text-muted mb-0">No barrels found for this wine.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {toppingData.destinationBarrels.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted">
                      {toppingData.destinationBarrels.length} barrels selected
                    </small>
                  </div>
                )}
              </div>

              {/* Container Reassignment Section */}
              {showReassignmentSection && (
                <div className="mt-4 p-3 border rounded bg-light">
                  <h6>Container Reassignment Plan</h6>
                  <p className="text-muted">
                    After topping, {remainingGallons.toFixed(2)} gallons will remain in the source container.
                    Select containers to reassign this wine to:
                  </p>
                  
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Container Type</th>
                          <th>Gallons</th>
                          <th>Include</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedReassignments.map((container, index) => (
                          <tr key={index}>
                            <td>{container.containerType}</td>
                            <td>{container.gallons}</td>
                            <td>
                              <input 
                                type="checkbox" 
                                className="form-check-input" 
                                checked={container.selected}
                                onChange={(e) => handleReassignmentChange(index, e.target.checked)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={handleAddTopping}
                disabled={
                  !toppingData.sourceBarrel || 
                  toppingData.destinationBarrels.length === 0 || 
                  !toppingData.gallonsUsed
                }
              >
                Add Topping Activity
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BarrelToppingModal;